import { defineNuxtPlugin } from "#app";
import { createApp } from "vue";
import Loading from "../private/Loading.vue";
import { useLoadingStore } from "../private/loading-store";
import { isFunction } from "lodash";

type Loader = { (loading?: boolean | string, hotText?: string): void };

type AnyFunction = { (...a: any[]): any };

export default defineNuxtPlugin(() => {
  const store = useLoadingStore();
  const vm = createApp(Loading);

  const el = document.createElement("div");
  // this.$refs.container.appendChild(el);
  document.body.appendChild(el);
  vm.mount(el);

  /**
   * Controls the global loading page and optional loading text.
   * @param input - When boolean: sets loading state. When string: enables loading and sets as loading text.
   * @param input2 - Optional loading text when first parameter is boolean.
   * @example
   * loading(true) // Show loading
   * loading(false) // Hide loading
   * loading(true, 'Processing...') // Show loading with text
   * loading('Processing...') // Show loading with text (shorthand)
   */
  const loading: Loader = (input?: string | boolean, input2?: string) => {
    const isString = typeof input === "string";
    store.loading = isString ? true : !!input;
    store.hotText = isString ? input : input2;
  };

  /**
   * Executes an async function or promise while displaying a loading indicator.
   * The loading page is automatically managed - shown when execution starts and hidden when it completes.
   * @param fn - The async function or promise to execute.
   * @returns A promise that resolves or rejects with the result of the provided function.
   * @example
   * await asyncLoading(async () => {
   *   return await fetchData();
   * });
   */
  const asyncLoading = async (fn: AnyFunction) => {
    return new Promise((resolve, reject) => {
      store.loading = true;
      const finished = (cb: AnyFunction) => {
        return (result: any) => {
          cb(result);
          store.loading = false;
        };
      };
      Promise.resolve(typeof fn === "function" ? fn() : fn)
        .then(finished(resolve))
        .catch(finished(reject));
    });
  };

  /**
   * Wraps an async function to automatically show loading page when called.
   * Returns a new function that, when invoked, will display a loading indicator during execution.
   * @param fn - The async function to wrap.
   * @param loadingText - Optional loading text. Can be a static string or a function that receives the same arguments as fn and returns a string.
   * @returns A wrapped version of the function that manages loading page automatically.
   * @example
   * const saveData = wrapLoading(
   *   async (data) => await api.save(data),
   *   'Saving...'
   * );
   * await saveData(myData); // Will show "Saving..." while executing
   *
   * // With dynamic text:
   * const deleteItem = wrapLoading(
   *   async (id) => await api.delete(id),
   *   (id) => `Deleting item ${id}...`
   * );
   */
  const wrapLoading = <A extends any[], R>(
    fn: { (...a: A): Promise<R> },
    loadingText?: string | { (...a: A): string }
  ) => {
    return async (...a: A): Promise<R> => {
      const text = isFunction(loadingText) ? loadingText(...a) : loadingText;
      return new Promise((resolve, reject) => {
        loading(true, text);
        fn(...a)
          .then(resolve)
          .catch(reject)
          .finally(() => {
            loading(false);
          });
      });
    };
  };

  return {
    provide: {
      loading,
      asyncLoading,
      wrapLoading,
    },
  };
});
